import { useEffect, useMemo, useRef, useState } from 'react';
import './App.scss';

import { LoadingScreenProvider } from './Store/LoadingManager';
import DialogManager from './Store/DialogManager';

import {
  QueryClient,
  QueryCache,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { DefaultTheme, ThemeProvider as StyledThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider, PaletteMode, createTheme } from "@mui/material/styles";

import LocalStorageHelper from './Utilities/Helpers/LocalStorageHelper';
import GlobalFunctionHelper from './Utilities/Helpers/GlobalFunctionHelper';

import { ErrorMessage } from './Constants/Messages';

import FabllbackProvider from './Views/PageLayout/FallbackProvider';
import AppRoutes from './Views/PageLayout/AppRoutes';
import { CssBaseline } from '@mui/material';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      refetchOnReconnect: true,
      staleTime: Infinity,
      // keepPreviousData: true
      // networkMode: 'offlineFirst'
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      console.error('Caching Error: ', error.message);
    }
  }),
})

export default function App() {

  const createDialog = () => { };
  const closeDialog = () => { };
  const dialogContextValue = useRef([createDialog, closeDialog]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const darkModeBroadcast = new BroadcastChannel("color-scheme");
  let isDarkMode = LocalStorageHelper.isDarkMode();
  //refs

  // Use states
  const [ChangesTriggered, setChangesTriggered] = useState(true);
  const [mode, setMode] = useState<PaletteMode>(isDarkMode ? "dark" : "light");
  const [dialogs, setDialogs] = useState([]);

  //Event Handlers
  const initializeWebSettings = async () => {
    let initWebTheme = await GlobalFunctionHelper.initializeWebTheme();

    // if (initWebTheme?.showExpiredTokenNotif) { //TODO use broadcast
    //   enqueueSnackbar(ErrorMessage.EXPIRED_LOGGEDIN_SESSION, { variant: 'error' });
    // }
  }

  useEffect(() => {
    initializeWebSettings();
  }, []);

  useEffect(() => {
    darkModeBroadcast.onmessage = (event) => { //listen for dark mode broadcast
      setMode(event.data as PaletteMode);
    };
    return () => darkModeBroadcast.close();
  }, []);

  window.addEventListener('uData', () => {
        setChangesTriggered(!ChangesTriggered);

        window.removeEventListener("uData", () => { return true; }, true);
    });

  const lightTheme: DefaultTheme = {
    primaryText: "var(--textPrimaryColor)",
    selectedText: '#25a0e2',
    secondaryText: "var(--subTextPrimaryColor)",
    errorText: '#f06548',
    border: "var(--darkTextPrimaryColor)",
    disabled: "var(--menuBtnBGHoverColor)",
    fieldBackgroundColor: "white",
    menuText: "var(--fieldLabelTextPrimaryColor)",
    pageBackground: "var(--cardBgPrimaryColor)",
    customBorder: {
      main: '#0000001f', // Example custom color
      blueBorder: 'var(--textInfoColor)',
      errorBorder: '#d32f2f',
    },
    customBackground: {
      main: '#fff',
      invoice: '#f9fbfc',
      stepperSelected: '#25a0e21a',
    },
  };

  const darkTheme: DefaultTheme = {
    primaryText: "var(--darkTextPrimaryColor)",
    selectedText: '#25a0e2',
    secondaryText: "var(--darkpopoverMenuBtnTextColor)",
    errorText: '#f06548',
    border: "var(--iconSecondaryColor)",
    disabled: "var(--darkFieldDisabledBGPrimaryColor)",
    fieldBackgroundColor: 'transparent', //"var(--darkFieldBackground)",
    menuText: "var(--darkTextPrimaryColor)",
    pageBackground: "var(--darkMainBGSecondarkColor)",
    customBorder: {
      main: '#ffffff1f', // Example custom color
      blueBorder: 'var(--textInfoColor)',
      errorBorder: '#d32f2f',
    },
    customBackground: {
      main: '#1d2021',
      invoice: '#1b1d1e',
      stepperSelected: '#1c2a30',
    },
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode == 'light' ? lightTheme : darkTheme)
        },
      }),
    [mode]
  );

  //Rendering UIs
  return <MuiThemeProvider theme={theme}>
    <StyledThemeProvider theme={theme}>
    <CssBaseline />
    {/* <Suspense fallback={<Loader />}> */}
    <FabllbackProvider>
      <LoadingScreenProvider>
        <div className={`App${(isDarkMode ? ' darkMode' : '')}`}>
          <QueryClientProvider client={queryClient}>
            <DialogManager>
              <AppRoutes />
            </DialogManager>
          </QueryClientProvider>
        </div>
      </LoadingScreenProvider>
      {/* </Suspense> */}
    </FabllbackProvider>
    </StyledThemeProvider>
  </MuiThemeProvider>
}
